import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AddCircleBlue, CustomDelete, FullTable, Loader, NoItemsYet, SimpleTooltip } from 'components';
import { appointmentActions, formActions, noteActions, httpRequestsOnSuccessActions } from 'store';
import { FindLoad, FindSuccessItem, Images, PermissionList, RolePermission, useModal } from 'utils';
import moment from 'moment/moment';
import { apptSessionNotesBody, apptSessionNotesHead, noteEnums, sessionNoteNotYet } from './constants';
import { RequestNoteUpdate } from '../../../../client/clientSingle/core/notes/requestNoteUpdate';
import { CreateAppSessionNote } from './createAppSessionNote';
import { SessionNoteDetails } from '../../../../client/clientSingle/core/notes/sessionNoteDetails';
import { useFutureApptWarning } from './useFutureApptWarning';
import { formBuilderStatusEnums, getServiceTemplate } from 'pages/forms/constants';

const ACTION_TYPE = 'DELETE_APPT_NOTE';
const ADD_EDIT_ACTION_TYPE = 'ADD_EDIT_APPT_NOTE';
const REQUEST_NOTE_ACTION_TYPE = 'REQUEST_APPT_NOTE';
const REMIND_NOTE_ACTION_TYPE = 'REMIND_APPT_NOTE';
const GET_NOTES_ACTION_TYPE = 'GET_GLOBAL_NOTES';
const APPT_ON_MODEL = 'Appt';

export const ApptSessionNotes = ({ item, fromComplete }) => {
    const { open } = useModal();
    const dispatch = useDispatch();
    const allNotes = useSelector((state) => state.note?.notes?.data) || [];
    const noteList = allNotes
        .filter((note) => note?.type === noteEnums.CLINICAL_NOTES)
        .map((note) => ({ ...note, createdDate: note?.created }));
    const deleteSuccess = FindSuccessItem(ACTION_TYPE);
    const addEditSuccess = FindSuccessItem(ADD_EDIT_ACTION_TYPE);
    const requestSuccess = FindSuccessItem(REQUEST_NOTE_ACTION_TYPE);
    const remindSuccess = FindSuccessItem(REMIND_NOTE_ACTION_TYPE);
    const loader = FindLoad(GET_NOTES_ACTION_TYPE);

    const getNotes = (load) => {
        if (item?.id) {
            dispatch(noteActions.getGlobalNotes(item.id, APPT_ON_MODEL, { limit: 100 }, load));
        }
    };

    useEffect(() => {
        getNotes();
    }, [item?.id]);

    useEffect(() => {
        if (deleteSuccess || addEditSuccess || requestSuccess || remindSuccess) {
            getNotes('noLoad');
            [ACTION_TYPE, ADD_EDIT_ACTION_TYPE, REQUEST_NOTE_ACTION_TYPE, REMIND_NOTE_ACTION_TYPE].forEach((type) =>
                dispatch(httpRequestsOnSuccessActions.removeSuccess(type)),
            );
        }
    }, [deleteSuccess, addEditSuccess, requestSuccess, remindSuccess]);

    const serviceTemplate = getServiceTemplate(item?.authorizedService?.service);

    useEffect(() => {
        dispatch(formActions.resetForms());
        dispatch(formActions.getForms({ model: noteEnums.CLINICAL_NOTES, status: formBuilderStatusEnums.PUBLISHED }));
    }, []);

    const handleClickActions = (note, name) => {
        if (name === 'requestUpdate') {
            open(<RequestNoteUpdate afterSuccess={() => {
            }} info={note} appt={{ ...item, apptId: item?.id }} single={true} />);
        }
    };

    const isFutureAppt = item?.startDate && moment.utc(item.startDate).startOf('day').isAfter(moment.utc().startOf('day'));
    const canManageFutureAppt = RolePermission([PermissionList.APPT_MANAGE_FUTURE?.code]);
    const blockFutureAppt = isFutureAppt && !canManageFutureAppt;
    const checkFutureDateAndRun = useFutureApptWarning(item);

    const handleAddNote = () => {
        if (blockFutureAppt) return;
        checkFutureDateAndRun(() =>
            open(
                <CreateAppSessionNote
                    appt={item}
                    noteType={noteEnums?.CLINICAL_NOTES}
                    serviceTemplate={serviceTemplate}
                />, {
                    styles: { bodyStyle: { padding: '24px 48px' } },
                },
            ),
        );
    };

    return (
        <div>
            <div className="space-between-align-center">
                <p className="note-table-title">Session Notes</p>
                <SimpleTooltip
                    disableHoverListener={!blockFutureAppt}
                    disableTouchListener={!blockFutureAppt}
                    title={<p>Cannot add notes to future appointments.</p>}
                    placement="top"
                >
                    <div>
                        <AddCircleBlue type="button" text={'Add Note'} handleClick={handleAddNote} disabled={!!blockFutureAppt} />
                    </div>
                </SimpleTooltip>
            </div>

            {loader?.length ? (
                <Loader />
            ) : noteList?.length ? (
                <FullTable
                    margin={'16px 0 0'}
                    height={fromComplete ? 'extraSmall' : 'medium'}
                    head={apptSessionNotesHead}
                    body={apptSessionNotesBody(item, serviceTemplate)}
                    list={noteList}
                    handleClickButton={(value, name) => handleClickActions(value, name)}
                    handleClick={(id, note) => {
                        open(
                            <SessionNoteDetails
                                serviceTemplate={serviceTemplate}
                                single={true}
                                note={note}
                                appt={item}
                                noteType={noteEnums?.CLINICAL_NOTES}
                                afterSuccess={() => {}}
                            />,
                            {
                                styles: { bodyStyle: { padding: '24px 48px' } },
                            }
                        );
                    }}
                    notYet={sessionNoteNotYet}
                    activeRowId={''}
                />
            ) : fromComplete ? (
                item?.requireNote ? (
                    <div className="session-note-required-flow">
                        <div className="session-note-required-box">
                            <div className="session-note-required-icon-flow">
                                <img src={Images.infoCircle} alt="info" />
                            </div>
                            <p className="session-note-required-title">Session Note Required</p>
                            <p className="session-note-required-desc">
                                At least one completed session note is required before this <br /> appointment can be completed.
                            </p>
                        </div>
                    </div>
                ) : (
                    <NoItemsYet
                        height={'150px'}
                        text={sessionNoteNotYet.title}
                        subTitle={sessionNoteNotYet.subTitle}
                        image={sessionNoteNotYet.image}
                    />
                )
            ) : (
                <NoItemsYet
                    height={sessionNoteNotYet?.height}
                    text={sessionNoteNotYet.title}
                    subTitle={sessionNoteNotYet.subTitle}
                    image={sessionNoteNotYet.image}
                />
            )}
        </div>
    );
};
